import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { AccountEntry } from '#src/components/mySorare/TransactionsHistory/SpentFiatPaymentIntents/AccountEntry/index.tsx';
import { TransactionsBid } from '#src/components/mySorare/TransactionsHistory/common/TransactionsBid/index.tsx';

import type { Auction_authorizationRequest } from './__generated__/index.graphql.ts';

type Props = {
  payment: Auction_authorizationRequest;
};

export const Auction = ({ payment }: Props) => {
  const { operation } = payment;
  const { formatMessage } = useIntl();
  const { toMonetaryAmount, getUserAmount } = useMonetaryAmount();

  if (!isType(operation, 'TokenAuction')) {
    return null;
  }

  return (
    <AccountEntry
      payment={{
        ...payment,
        // Spreads launder the AuthorizationRequest union's excess-property
        // check; `amount` only exists on some variants.
        // oxlint-disable-next-line unicorn/no-useless-spread
        ...{
          request: {
            ...payment.request,
            // oxlint-disable-next-line unicorn/no-useless-spread
            ...{
              amount: getUserAmount(
                toMonetaryAmount(operation.bestBid?.amounts)
              ),
            },
          },
        },
      }}
      title={formatMessage(glossary.auction)}
    >
      <TransactionsBid tokenBid={operation.bestBid!} />
    </AccountEntry>
  );
};

Auction.fragments = {
  payment: gql`
    fragment Auction_authorizationRequest on AuthorizationRequest {
      id
      ...AccountEntry_authorizationRequest
      operation {
        ... on TokenAuction {
          id
          bestBid {
            id
            amounts {
              ...useMonetaryAmount_monetaryAmount
            }
            ...TransactionsBid_tokenBid
          }
        }
      }
    }
    ${AccountEntry.fragments.payment}
    ${TransactionsBid.fragments.tokenBid}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<Auction_authorizationRequest>,
};
